import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { keys } from '@/lib/queryKeys';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { RowActionsMenu } from '@/components/ui/RowActionsMenu';
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Switch } from '@/components/ui/Switch';
import { toast } from '@/hooks/useToast';
import {
  createPharmacyStore, fetchPharmacyStoresAdmin, setPharmacyChannels, updatePharmacyStore,
} from '@/features/pharmacy/api';
import type { PharmacyChannel, PharmacyStore, PharmacyStoreType } from '@/features/pharmacy/types';
import { SettingsToolbar, filterByStatus, matchesSearch, type StatusFilter } from '../shared/SettingsToolbar';

/**
 * Admin → Pharmacy & Lab → Pharmacy Stores (admin:pharmacy-stores).
 *
 * The stores at the ACTIVE branch — Main Store, OP Pharmacy, IP Pharmacy, … —
 * and which store each counter sells from. Every branch starts with one store,
 * Main Pharmacy, used for everything; adding an OP and an IP store and
 * pointing the counters at them is what splits the pharmacy.
 * Design: docs/HMS_V4_PHARMACY_MULTI_STORE_DESIGN.md. Every rule is re-checked
 * by utils/pharmacyStores.js — this screen only explains them.
 */

const TYPE_LABEL: Record<PharmacyStoreType, string> = {
  main: 'Main store', op: 'OP pharmacy', ip: 'IP pharmacy', satellite: 'Satellite (OT, ICU, ER …)',
};

// Display order of the channel picker — receiving first, it feeds the rest.
const CHANNEL_ORDER: PharmacyChannel[] = ['receiving', 'visit', 'outpatient', 'admission', 'ip_order', 'borrow'];
const CHANNEL_HINT: Record<PharmacyChannel, string> = {
  receiving: 'Vendor goods receipts and the Excel inventory upload land here.',
  visit: 'Dispensing tab and the Visit Workflow’s Pharmacy stage.',
  outpatient: 'Walk-in counter sales.',
  admission: 'IP Pharmacy counter (attenders buying for an admitted patient).',
  ip_order: 'Dispensing against a medication order from the IP Case Sheet.',
  borrow: 'Medicine lent to another hospital.',
};
const eligible = (s: PharmacyStore, ch: PharmacyChannel) => s.active && (ch === 'receiving' ? s.can_receive : s.can_dispense);

export default function PharmacyStoresTab() {
  const { user, branchVersion } = useAuth();
  const qc = useQueryClient();
  const queryKey = [...keys.pharmacy.storesAdmin(), branchVersion];
  const query = useQuery({ queryKey, queryFn: fetchPharmacyStoresAdmin });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [editing, setEditing] = useState<PharmacyStore | 'new' | null>(null);
  const [confirmDeactivate, setConfirmDeactivate] = useState<PharmacyStore | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: keys.pharmacy.stores() });
    qc.invalidateQueries({ queryKey: keys.pharmacy.inventory() });
  };
  const toggle = useMutation({
    mutationFn: ({ store, active }: { store: PharmacyStore; active: boolean }) => updatePharmacyStore(store.id, { active }),
    onSuccess: (s) => { toast({ title: `${s.name} ${s.active ? 'reactivated' : 'deactivated'}`, variant: 'success' }); refresh(); },
    onError: (e: Error) => toast({ title: 'Not saved', description: e.message, variant: 'danger' }),
  });

  const stores = useMemo(() => query.data?.stores ?? [], [query.data]);
  const labels = query.data?.channel_labels;
  const rows = useMemo(
    () => filterByStatus(stores, status).filter((s) => matchesSearch(search, s.code, s.name, TYPE_LABEL[s.store_type])),
    [stores, status, search]
  );

  const columns: DataTableColumn<PharmacyStore>[] = [
    { key: 'code', header: 'Code', widthClassName: 'w-[90px]', sortable: true, sortValue: (s) => s.code,
      render: (s) => <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11.5px] font-bold">{s.code}</span> },
    { key: 'name', header: 'Name', sortable: true, sortValue: (s) => s.name,
      render: (s) => (
        <div>
          <div className="font-semibold">{s.name}</div>
          <div className="text-[11.5px] text-muted-foreground">{TYPE_LABEL[s.store_type]}</div>
        </div>
      ) },
    { key: 'can', header: 'Can', widthClassName: 'w-[170px]',
      render: (s) => (
        <span className="flex flex-wrap gap-1">
          {s.can_receive && <Badge variant="info">Receive</Badge>}
          {s.can_dispense && <Badge variant="info">Dispense</Badge>}
          {!s.can_receive && !s.can_dispense && <Badge variant="neutral">Hold only</Badge>}
        </span>
      ) },
    { key: 'used', header: 'Used for',
      render: (s) => s.channels.length === CHANNEL_ORDER.length
        ? <Badge variant="neutral">All counters</Badge>
        : s.channels.length
        ? <span className="flex flex-wrap gap-1">{s.channels.map((c) => <Badge key={c} variant="neutral">{labels?.[c] ?? c}</Badge>)}</span>
        : <span className="text-[12px] text-muted-foreground">Not used by any counter</span> },
    { key: 'stock', header: 'Stock', widthClassName: 'w-[140px]', align: 'right',
      render: (s) => <span className="tabular-nums">{s.units_in_stock.toLocaleString('en-IN')} units<span className="block text-[11.5px] text-muted-foreground">{s.batch_count} batch{s.batch_count === 1 ? '' : 'es'}</span></span> },
    { key: 'status', header: 'Status', widthClassName: 'w-[100px]',
      render: (s) => <Badge variant={s.active ? 'success' : 'neutral'}>{s.active ? 'Active' : 'Inactive'}</Badge> },
    { key: 'actions', header: <span className="sr-only">Actions</span>, align: 'right', widthClassName: 'w-[56px]',
      render: (s) => {
        const blocker = s.channels.length ? 'Point its counters at another store first'
          : s.units_in_stock > 0 ? 'Still holds stock — move or write it off first' : undefined;
        return (
          <RowActionsMenu
            label={`Actions for ${s.name}`}
            actions={[
              { label: 'Edit details', onSelect: () => setEditing(s) },
              s.active
                ? { label: 'Deactivate…', danger: true, disabled: !!blocker, hint: blocker, onSelect: () => setConfirmDeactivate(s) }
                : { label: 'Reactivate', onSelect: () => toggle.mutate({ store: s, active: true }) },
            ]}
          />
        );
      } },
  ];

  return (
    <div className="space-y-4">
      <p className="text-[12.5px] text-muted-foreground">
        Stores at <b>{user?.active_branch?.name ?? 'this branch'}</b>. Each branch has its own stores — switch branch at the top of the screen to set up another.
      </p>
      <div>
        <SettingsToolbar
          search={search}
          onSearch={setSearch}
          placeholder="Search stores"
          status={status}
          onStatus={setStatus}
          count={query.data ? `${rows.length} store${rows.length === 1 ? '' : 's'}` : undefined}
          action={<Button onClick={() => setEditing('new')}><Plus className="h-4 w-4" /> Add store</Button>}
        />
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(s) => s.id}
          isLoading={query.isLoading}
          isError={query.isError}
          errorMessage={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => query.refetch()}
          emptyLabel={search ? 'No stores match' : status === 'inactive' ? 'No inactive stores' : 'No stores yet'}
        />
      </div>

      {query.data && labels && (
        <ChannelMapCard
          stores={stores}
          channels={query.data.channels}
          labels={labels}
          onSaved={(data) => { qc.setQueryData(queryKey, data); refresh(); }}
        />
      )}

      <StoreSheet
        store={editing}
        onClose={() => setEditing(null)}
        onSaved={(msg) => { setEditing(null); toast({ title: msg, variant: 'success' }); qc.invalidateQueries({ queryKey: keys.pharmacy.storesAdmin() }); refresh(); }}
      />

      <ConfirmDialog
        open={!!confirmDeactivate}
        onOpenChange={(o) => { if (!o) setConfirmDeactivate(null); }}
        title={`Deactivate ${confirmDeactivate?.name ?? ''}?`}
        description="It will no longer appear in store pickers and no counter can be pointed at it. Its stock history stays, and you can reactivate it later."
        confirmLabel="Deactivate"
        onConfirm={() => { const s = confirmDeactivate; if (s) toggle.mutate({ store: s, active: false }); }}
      />
    </div>
  );
}

function ChannelMapCard({ stores, channels, labels, onSaved }: {
  stores: PharmacyStore[];
  channels: Partial<Record<PharmacyChannel, number>>;
  labels: Record<PharmacyChannel, string>;
  onSaved: (data: Awaited<ReturnType<typeof setPharmacyChannels>>) => void;
}) {
  const [draft, setDraft] = useState(channels);
  useEffect(() => { setDraft(channels); }, [channels]);
  const changed = CHANNEL_ORDER.filter((c) => draft[c] !== channels[c]);
  const save = useMutation({
    mutationFn: () => setPharmacyChannels(Object.fromEntries(changed.map((c) => [c, draft[c]]))),
    onSuccess: (data) => { toast({ title: 'Counters updated', variant: 'success' }); onSaved(data); },
    onError: (e: Error) => toast({ title: 'Not saved', description: e.message, variant: 'danger' }),
  });

  return (
    <Card>
      <CardHeader><CardTitle>Which store each counter uses</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-[12.5px] text-muted-foreground">
          A sale only takes stock from its counter’s store. Staff can’t change this on the counter itself.
        </p>
        <div className="divide-y divide-border rounded-[var(--radius)] border border-border">
          {CHANNEL_ORDER.map((c) => {
            const options = stores.filter((s) => eligible(s, c) || s.id === channels[c]);
            return (
              <div key={c} className="flex flex-col gap-2 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-[13px] font-semibold">{labels[c]}</div>
                  <div className="text-[12px] text-muted-foreground">{CHANNEL_HINT[c]}</div>
                </div>
                <select
                  aria-label={labels[c]}
                  className="h-9 w-full rounded-[var(--radius)] border border-input bg-card px-2 text-[13px] sm:w-[220px]"
                  value={draft[c] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [c]: Number(e.target.value) }))}
                >
                  {options.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2">
          {changed.length > 0 && <Button variant="ghost" onClick={() => setDraft(channels)}>Discard</Button>}
          <Button disabled={!changed.length} loading={save.isPending} onClick={() => save.mutate()}>
            {changed.length ? `Save ${changed.length} change${changed.length === 1 ? '' : 's'}` : 'No changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

const EMPTY = { code: '', name: '', store_type: 'op' as PharmacyStoreType, can_receive: false, can_dispense: true, auto_receive_transfers: false };

function StoreSheet({ store, onClose, onSaved }: { store: PharmacyStore | 'new' | null; onClose: () => void; onSaved: (msg: string) => void }) {
  const isNew = store === 'new';
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (!store) return;
    setError(''); setTouched(false);
    setForm(store === 'new' ? EMPTY : {
      code: store.code, name: store.name, store_type: store.store_type, can_receive: store.can_receive, can_dispense: store.can_dispense,
      auto_receive_transfers: store.auto_receive_transfers,
    });
  }, [store]);

  const codeError = touched && isNew && !/^[A-Za-z0-9-]{1,10}$/.test(form.code.trim()) ? '1–10 letters, digits or hyphens' : undefined;
  const nameError = touched && !form.name.trim() ? 'Enter the store name' : undefined;

  const save = async () => {
    setTouched(true);
    if (!form.name.trim() || (isNew && !/^[A-Za-z0-9-]{1,10}$/.test(form.code.trim()))) return;
    setSaving(true); setError('');
    try {
      const fields = {
        name: form.name.trim(), store_type: form.store_type, can_receive: form.can_receive, can_dispense: form.can_dispense,
        auto_receive_transfers: form.auto_receive_transfers,
      };
      if (isNew) {
        await createPharmacyStore({ code: form.code.trim().toUpperCase(), ...fields });
        onSaved(`${fields.name} added`);
      } else if (store) {
        await updatePharmacyStore(store.id, fields);
        onSaved(`${fields.name} saved`);
      }
    } catch (e: any) {
      setError(e.message);
    }
    setSaving(false);
  };

  return (
    <Sheet open={!!store} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{isNew ? 'Add store' : 'Edit store'}</SheetTitle>
          <SheetDescription>{isNew ? 'A separate stock location at this branch.' : `Store ${typeof store === 'object' && store ? store.code : ''}`}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {error && <div className="rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger-ink">{error}</div>}
          <FormField label="Code" required error={codeError}
            hint={isNew ? 'Short, e.g. IP or OP. It can’t be changed later.' : 'The code can’t be changed after the store is created.'}>
            <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} maxLength={10} disabled={!isNew}
              placeholder="e.g. IP" className="font-mono uppercase placeholder:normal-case placeholder:font-sans" autoFocus={isNew} />
          </FormField>
          <FormField label="Name" required error={nameError}>
            <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. IP Pharmacy" autoFocus={!isNew} />
          </FormField>
          <FormField label="Type">
            <select
              className="h-9 w-full rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]"
              value={form.store_type}
              onChange={(e) => setForm((f) => ({ ...f, store_type: e.target.value as PharmacyStoreType }))}
            >
              {(Object.keys(TYPE_LABEL) as PharmacyStoreType[]).map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
            </select>
          </FormField>
          <label className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
            <span>
              <span className="block text-[13px] font-semibold">Receives vendor goods</span>
              <span className="block text-[12px] text-muted-foreground">Usually only the Main Store. Other stores are filled by transfer.</span>
            </span>
            <Switch checked={form.can_receive} onCheckedChange={(v) => setForm((f) => ({ ...f, can_receive: v }))} />
          </label>
          <label className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
            <span>
              <span className="block text-[13px] font-semibold">Dispenses to patients</span>
              <span className="block text-[12px] text-muted-foreground">Needed for a counter to sell from it.</span>
            </span>
            <Switch checked={form.can_dispense} onCheckedChange={(v) => setForm((f) => ({ ...f, can_dispense: v }))} />
          </label>
          <label className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
            <span>
              <span className="block text-[13px] font-semibold">Receive transfers automatically</span>
              <span className="block text-[12px] text-muted-foreground">For a store that shares a room with the sender: a transfer here is received the moment it is issued. Leave off for the usual two-step handover.</span>
            </span>
            <Switch checked={form.auto_receive_transfers} onCheckedChange={(v) => setForm((f) => ({ ...f, auto_receive_transfers: v }))} />
          </label>
          {isNew && (
            <p className="text-[12px] text-muted-foreground">A new store starts empty. Move stock into it under Pharmacy → Stock Transfers before pointing a counter at it.</p>
          )}
        </SheetBody>
        <SheetFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} loading={saving}>{isNew ? 'Add store' : 'Save changes'}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
